import { useState } from 'react';

import { Add, Delete } from '@mui/icons-material';
import {
	Alert,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';

import { DialogHeaderActions } from '@@components';

import GenericPropertyInput from '../../../FlowDesigner/Components/Inputs/GenericPropertyInput';
import { ManagedChatConfigurationModel } from '../../Models/ManagedChatConfigurationModel';

const defaultPolicy: ManagedChatConfigurationModel = {
	AllowNewConversation: true,
	ShowSessionHistory: true,
	ShowAgentActivity: false,
	AllowAttachments: false,
	AllowFileReads: false,
	AllowFileUploads: false,
};
const policyFields = [
	{ key: 'AllowNewConversation', label: 'Allow new conversations' },
	{ key: 'ShowSessionHistory', label: 'Allow session history' },
	{ key: 'ShowAgentActivity', label: 'Show agent activity' },
	{ key: 'AllowAttachments', label: 'Allow message attachments' },
	{ key: 'AllowFileReads', label: 'Allow file reads' },
	{ key: 'AllowFileUploads', label: 'Allow file uploads' },
] as const;
type Props = { value?: ManagedChatConfigurationModel | null; onValueChange(value: ManagedChatConfigurationModel | undefined): void };

function ManagedChatDialog({ value, onValueChange, onClose }: Props & { onClose(): void }) {
	const [enabled, setEnabled] = useState(value != null);
	const [policy, setPolicy] = useState(() => ({ ...defaultPolicy, ...value }));
	const [attributes, setAttributes] = useState(() => Object.entries(value?.ConversationAttributes ?? {}).map(([name, content], id) => ({ id, name, content })));
	const [maximized, setMaximized] = useState(false);
	const names = attributes.map((attribute) => attribute.name.trim());
	const invalidAttributes = names.some((name) => !name) || new Set(names).size !== names.length;
	return (
		<Dialog open fullWidth maxWidth="sm" fullScreen={maximized} onClose={onClose} aria-labelledby="managed-chat-title">
			<DialogTitle id="managed-chat-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography component="span" variant="h6" sx={{ flex: 1 }}>
					Managed Chat
				</Typography>
				<DialogHeaderActions isMaximized={maximized} onToggleMaximized={() => setMaximized((current) => !current)} onClose={onClose} />
			</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={2}>
					<FormControlLabel label="Enable managed chat" control={<Switch checked={enabled} onChange={(_, checked) => setEnabled(checked)} />} />
					<Alert severity="info">
						The React component supplies the agent when it opens a session. These settings belong to the App draft and take effect after saving. Disabling
						managed chat removes this host's Chat policy.
					</Alert>
					<Stack>
						{policyFields.map(({ key, label }) => (
							<FormControlLabel
								key={key}
								label={label}
								control={
									<Switch
										disabled={!enabled}
										checked={policy[key] === true}
										onChange={(_, checked) => setPolicy((current) => ({ ...current, [key]: checked }))}
									/>
								}
							/>
						))}
					</Stack>
					<Typography variant="body2" color="text.secondary">
						File access also requires permission in the App settings. Anonymous sessions require an App Embed; a public View alone does not establish a session.
					</Typography>
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Typography variant="subtitle2">Conversation attributes</Typography>
						<Button
							disabled={!enabled}
							startIcon={<Add />}
							onClick={() => setAttributes((current) => [...current, { id: Math.max(-1, ...current.map((item) => item.id)) + 1, name: '', content: '' }])}
						>
							Add attribute
						</Button>
					</Stack>
					{attributes.map((attribute, index) => (
						<Stack key={attribute.id} direction="row" spacing={1} alignItems="center">
							<TextField
								disabled={!enabled}
								label={`Attribute name ${index + 1}`}
								value={attribute.name}
								size="small"
								onChange={(event) =>
									setAttributes((current) => current.map((item) => (item.id === attribute.id ? { ...item, name: event.target.value } : item)))
								}
							/>
							<TextField
								disabled={!enabled}
								label={`Attribute value ${index + 1}`}
								value={attribute.content}
								size="small"
								onChange={(event) =>
									setAttributes((current) => current.map((item) => (item.id === attribute.id ? { ...item, content: event.target.value } : item)))
								}
							/>
							<IconButton
								disabled={!enabled}
								aria-label={`Remove attribute ${index + 1}`}
								onClick={() => setAttributes((current) => current.filter((item) => item.id !== attribute.id))}
							>
								<Delete />
							</IconButton>
						</Stack>
					))}
					{enabled && invalidAttributes && <Alert severity="warning">Attribute names must be nonempty and unique.</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					disabled={enabled && invalidAttributes}
					onClick={() => {
						onValueChange(
							enabled
								? {
										...policy,
										ConversationAttributes: attributes.length
											? Object.fromEntries(attributes.map((attribute) => [attribute.name.trim(), attribute.content]))
											: undefined,
									}
								: undefined,
						);
						onClose();
					}}
				>
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default function ManagedChatInput({ value, onValueChange }: Props) {
	const [open, setOpen] = useState(false);
	return (
		<GenericPropertyInput value={value ?? undefined} onValueChange={onValueChange} onOpen={() => setOpen(true)}>
			<Chip size="small" label={value ? 'Enabled' : 'Disabled'} color={value ? 'success' : 'default'} />
			{open && <ManagedChatDialog value={value} onValueChange={onValueChange} onClose={() => setOpen(false)} />}
		</GenericPropertyInput>
	);
}
